export type BalanceGameItem = {
  id: string;
  question: string;
  leftLabel: string;
  rightLabel: string;
  audienceIds: BalanceGameAudienceId[];
  tagIds: BalanceGameTagId[];
};

export type BalanceGameAudienceId = "solo" | "couple" | "group";

export type BalanceGameAudienceColor = "info" | "secondary" | "success";

export type BalanceGameTagId =
  | "daily"
  | "taste"
  | "rest"
  | "travel"
  | "hobby"
  | "work"
  | "values"
  | "date"
  | "coupleTalk"
  | "friends"
  | "party"
  | "debate";

export type BalanceGameAudience = {
  id: BalanceGameAudienceId;
  label: string;
  shortLabel: string;
  description: string;
  accentColor: BalanceGameAudienceColor;
  defaultTagIds: BalanceGameTagId[];
};

export type BalanceGameTag = {
  id: BalanceGameTagId;
  label: string;
  description: string;
  audienceIds: BalanceGameAudienceId[];
};

export const balanceGameAudiences: BalanceGameAudience[] = [
  {
    id: "solo",
    label: "1인",
    shortLabel: "1인",
    description: "혼자 빠르게 고르면서 취향과 생활 패턴을 확인하는 질문 위주예요.",
    accentColor: "info",
    defaultTagIds: ["daily", "taste", "rest", "travel", "hobby", "values"],
  },
  {
    id: "couple",
    label: "커플",
    shortLabel: "커플",
    description: "데이트, 연락, 취향 차이처럼 둘이 이야기하기 좋은 질문 위주예요.",
    accentColor: "secondary",
    defaultTagIds: ["date", "coupleTalk", "taste", "travel", "values"],
  },
  {
    id: "group",
    label: "단체",
    shortLabel: "단체",
    description: "친구나 모임에서 의견이 갈리기 쉬운 질문 위주예요.",
    accentColor: "success",
    defaultTagIds: ["friends", "party", "debate", "taste", "travel"],
  },
];

export const balanceGameTags: BalanceGameTag[] = [
  {
    id: "daily",
    label: "일상",
    description: "생활 습관과 평소 선택을 가볍게 고르는 질문",
    audienceIds: ["solo", "couple", "group"],
  },
  {
    id: "taste",
    label: "취향",
    description: "음식, 콘텐츠, 공간처럼 바로 대답하기 쉬운 질문",
    audienceIds: ["solo", "couple", "group"],
  },
  {
    id: "rest",
    label: "휴식",
    description: "쉬는 날과 혼자만의 시간을 다루는 질문",
    audienceIds: ["solo", "couple"],
  },
  {
    id: "travel",
    label: "여행",
    description: "여행 스타일과 일정 선택을 다루는 질문",
    audienceIds: ["solo", "couple", "group"],
  },
  {
    id: "hobby",
    label: "취미",
    description: "새로 해보고 싶은 활동과 취미 성향 질문",
    audienceIds: ["solo", "group"],
  },
  {
    id: "work",
    label: "업무/공부",
    description: "집중 방식과 협업 성향을 확인하는 질문",
    audienceIds: ["solo", "group"],
  },
  {
    id: "values",
    label: "가치관",
    description: "선호보다 조금 더 깊은 기준을 묻는 질문",
    audienceIds: ["solo", "couple", "group"],
  },
  {
    id: "date",
    label: "데이트",
    description: "커플이 바로 이야기하기 좋은 데이트 선택 질문",
    audienceIds: ["couple"],
  },
  {
    id: "coupleTalk",
    label: "연애 대화",
    description: "연락, 표현, 갈등처럼 둘 사이의 대화가 필요한 질문",
    audienceIds: ["couple"],
  },
  {
    id: "friends",
    label: "친구",
    description: "친구끼리 서로의 성향을 비교하기 좋은 질문",
    audienceIds: ["group"],
  },
  {
    id: "party",
    label: "모임",
    description: "여럿이 있을 때 분위기가 갈리는 질문",
    audienceIds: ["group"],
  },
  {
    id: "debate",
    label: "논쟁",
    description: "가볍게 의견이 갈리고 이유를 말하기 좋은 질문",
    audienceIds: ["group"],
  },
];

export const balanceGameItems: BalanceGameItem[] = [
  {
    id: "weekend-rest-or-out",
    question: "주말 하루를 통째로 즐긴다면 어느 쪽이 더 좋은가요?",
    leftLabel: "집에서 푹 쉬기",
    rightLabel: "밖에서 하루 종일 놀기",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["daily", "rest", "friends"],
  },
  {
    id: "rice-or-noodle",
    question: "평생 하나만 더 자주 먹어야 한다면 무엇을 고를 건가요?",
    leftLabel: "밥",
    rightLabel: "면",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "debate"],
  },
  {
    id: "planned-or-spontaneous-trip",
    question: "더 끌리는 여행 스타일은 어느 쪽인가요?",
    leftLabel: "계획 빡빡한 여행",
    rightLabel: "즉흥 여행",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["travel", "values"],
  },
  {
    id: "movie-or-series",
    question: "콘텐츠를 즐길 때 더 선호하는 것은 무엇인가요?",
    leftLabel: "영화 몰아보기",
    rightLabel: "드라마 정주행",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "daily"],
  },
  {
    id: "quiet-or-noise",
    question: "업무나 공부를 할 때 더 편한 환경은 무엇인가요?",
    leftLabel: "조용한 공간",
    rightLabel: "약간의 배경 소음",
    audienceIds: ["solo", "group"],
    tagIds: ["work", "daily"],
  },
  {
    id: "restaurant-or-cafe",
    question: "친구와 약속이 잡히면 더 마음이 가는 쪽은 무엇인가요?",
    leftLabel: "맛집 탐방",
    rightLabel: "카페 수다",
    audienceIds: ["couple", "group"],
    tagIds: ["taste", "date", "friends"],
  },
  {
    id: "earphones-or-charger",
    question: "하루 동안 하나만 쓸 수 있다면 무엇이 더 아쉬울까요?",
    leftLabel: "이어폰",
    rightLabel: "휴대폰 충전기",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["daily", "debate"],
  },
  {
    id: "active-or-calm-hobby",
    question: "취미를 새로 시작한다면 어떤 쪽이 더 끌리나요?",
    leftLabel: "몸 쓰는 취미",
    rightLabel: "집중하는 정적인 취미",
    audienceIds: ["solo", "group"],
    tagIds: ["hobby", "values"],
  },
  {
    id: "city-or-nature-trip",
    question: "여행을 떠난다면 어느 스타일이 더 끌리나요?",
    leftLabel: "도시 여행",
    rightLabel: "자연 여행",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["travel", "taste"],
  },
  {
    id: "spicy-or-sweet",
    question: "하루 종일 먹어야 한다면 어떤 쪽이 더 좋나요?",
    leftLabel: "매운 음식",
    rightLabel: "달달한 음식",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "debate"],
  },
  {
    id: "window-or-corner-seat",
    question: "카페에서 시간을 보낸다면 어떤 쪽이 더 좋은가요?",
    leftLabel: "창가 자리",
    rightLabel: "구석 조용한 자리",
    audienceIds: ["solo", "couple"],
    tagIds: ["daily", "rest", "date"],
  },
  {
    id: "planned-course-or-spontaneous-move",
    question: "친구들과 놀 때 더 선호하는 흐름은 무엇인가요?",
    leftLabel: "계획된 코스",
    rightLabel: "즉흥적으로 이동",
    audienceIds: ["group"],
    tagIds: ["friends", "party", "travel"],
  },
  {
    id: "silence-or-music",
    question: "집중이 더 잘 되는 환경은 어느 쪽인가요?",
    leftLabel: "완전한 정적",
    rightLabel: "잔잔한 음악",
    audienceIds: ["solo", "group"],
    tagIds: ["work", "daily"],
  },
  {
    id: "cake-or-icecream",
    question: "식사 후 디저트를 고른다면 더 자주 고르는 쪽은 무엇인가요?",
    leftLabel: "케이크",
    rightLabel: "아이스크림",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "date"],
  },
  {
    id: "sleep-in-or-early-out",
    question: "쉬는 날 더 끌리는 활동은 무엇인가요?",
    leftLabel: "늦잠 자기",
    rightLabel: "아침 일찍 나가기",
    audienceIds: ["solo", "couple"],
    tagIds: ["rest", "daily", "date"],
  },
  {
    id: "idea-or-finish",
    question: "팀플이나 협업에서 더 편한 역할은 어느 쪽인가요?",
    leftLabel: "아이디어 내기",
    rightLabel: "정리하고 마무리하기",
    audienceIds: ["solo", "group"],
    tagIds: ["work", "values"],
  },
  {
    id: "landscape-or-portrait",
    question: "사진을 남길 때 더 선호하는 것은 무엇인가요?",
    leftLabel: "풍경 사진",
    rightLabel: "인물 사진",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["travel", "taste"],
  },
  {
    id: "short-or-long-video",
    question: "영상을 볼 때 더 손이 가는 것은 무엇인가요?",
    leftLabel: "짧고 빠른 영상",
    rightLabel: "길고 몰입감 있는 영상",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "daily"],
  },
  {
    id: "small-talk-or-group-chat",
    question: "모임에서 더 편한 순간은 언제인가요?",
    leftLabel: "소수로 깊게 대화할 때",
    rightLabel: "여럿이 같이 떠들 때",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["friends", "party", "values"],
  },
  {
    id: "offline-or-online-shopping",
    question: "쇼핑을 한다면 어느 쪽이 더 가깝나요?",
    leftLabel: "오프라인 매장",
    rightLabel: "온라인 주문",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["daily", "taste"],
  },
  {
    id: "handwriting-or-phone-memo",
    question: "메모를 할 때 더 선호하는 방식은 무엇인가요?",
    leftLabel: "손글씨 메모",
    rightLabel: "휴대폰 메모",
    audienceIds: ["solo"],
    tagIds: ["daily", "work"],
  },
  {
    id: "solo-or-together-exercise",
    question: "운동을 한다면 더 끌리는 것은 무엇인가요?",
    leftLabel: "혼자 하는 운동",
    rightLabel: "같이 하는 운동",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["hobby", "date", "friends"],
  },
  {
    id: "soup-or-pancake-rain",
    question: "비 오는 날 더 생각나는 선택은 무엇인가요?",
    leftLabel: "따뜻한 국물",
    rightLabel: "전과 막걸리",
    audienceIds: ["solo", "couple", "group"],
    tagIds: ["taste", "daily", "party"],
  },
  {
    id: "lighting-or-furniture",
    question: "집 꾸미기를 한다면 더 먼저 챙기고 싶은 것은 무엇인가요?",
    leftLabel: "조명",
    rightLabel: "가구",
    audienceIds: ["solo", "couple"],
    tagIds: ["daily", "values"],
  },
  {
    id: "short-often-or-long-once",
    question: "연락 습관으로 더 가까운 쪽은 무엇인가요?",
    leftLabel: "짧게 자주 연락",
    rightLabel: "길게 한 번에 연락",
    audienceIds: ["couple", "group"],
    tagIds: ["coupleTalk", "friends", "values"],
  },
  {
    id: "solo-empty-day",
    question: "혼자 빈 하루가 생긴다면 어느 쪽이 더 끌리나요?",
    leftLabel: "밀린 정리 끝내기",
    rightLabel: "새로운 곳 가보기",
    audienceIds: ["solo"],
    tagIds: ["rest", "travel", "values"],
  },
  {
    id: "solo-money-or-time",
    question: "혼자만의 보상이 필요하다면 무엇이 더 좋나요?",
    leftLabel: "맛있는 한 끼",
    rightLabel: "방해 없는 낮잠",
    audienceIds: ["solo"],
    tagIds: ["rest", "taste", "daily"],
  },
  {
    id: "solo-new-skill-or-space",
    question: "지금 하나를 바꿀 수 있다면 어느 쪽이 더 좋나요?",
    leftLabel: "새로운 기술 배우기",
    rightLabel: "내 공간 업그레이드",
    audienceIds: ["solo"],
    tagIds: ["hobby", "work", "values"],
  },
  {
    id: "date-home-or-outside",
    question: "데이트 하루를 고른다면 어느 쪽이 더 좋나요?",
    leftLabel: "집에서 같이 쉬기",
    rightLabel: "밖에서 코스 돌기",
    audienceIds: ["couple"],
    tagIds: ["date", "rest"],
  },
  {
    id: "couple-anniversary",
    question: "기념일을 챙긴다면 어느 쪽이 더 마음에 드나요?",
    leftLabel: "미리 준비한 이벤트",
    rightLabel: "편하게 맛있는 식사",
    audienceIds: ["couple"],
    tagIds: ["date", "coupleTalk", "values"],
  },
  {
    id: "couple-conflict",
    question: "서로 의견이 안 맞을 때 더 나은 방식은 무엇인가요?",
    leftLabel: "바로 이야기하기",
    rightLabel: "각자 식히고 말하기",
    audienceIds: ["couple"],
    tagIds: ["coupleTalk", "values"],
  },
  {
    id: "couple-travel-role",
    question: "둘이 여행을 간다면 더 맡고 싶은 역할은 무엇인가요?",
    leftLabel: "일정 짜기",
    rightLabel: "현장에서 분위기 보기",
    audienceIds: ["couple"],
    tagIds: ["travel", "date", "coupleTalk"],
  },
  {
    id: "group-host-or-reactor",
    question: "모임에서 더 필요한 사람은 어느 쪽이라고 생각하나요?",
    leftLabel: "진행 잘하는 사람",
    rightLabel: "리액션 좋은 사람",
    audienceIds: ["group"],
    tagIds: ["party", "friends", "debate"],
  },
  {
    id: "group-food-or-activity",
    question: "여럿이 모일 때 더 중요한 것은 무엇인가요?",
    leftLabel: "음식이 맛있어야 함",
    rightLabel: "할 게 재미있어야 함",
    audienceIds: ["group"],
    tagIds: ["party", "taste", "debate"],
  },
  {
    id: "group-trip-room",
    question: "단체 여행 숙소를 고른다면 어느 쪽을 더 중시하나요?",
    leftLabel: "방이 넓고 쾌적함",
    rightLabel: "위치가 압도적으로 좋음",
    audienceIds: ["group"],
    tagIds: ["travel", "friends", "debate"],
  },
  {
    id: "group-late-friend",
    question: "친구가 약속에 늦는다면 어느 쪽이 더 낫나요?",
    leftLabel: "늦어도 미리 말하기",
    rightLabel: "늦었지만 커피 사오기",
    audienceIds: ["group"],
    tagIds: ["friends", "debate"],
  },
  {
    id: "group-game-style",
    question: "모임 게임을 한다면 어느 쪽이 더 재미있나요?",
    leftLabel: "전략으로 이기는 게임",
    rightLabel: "운으로 터지는 게임",
    audienceIds: ["group"],
    tagIds: ["party", "debate"],
  },
];
